import { useEffect, useState } from "react";
import { FileText, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { PageTitle } from "@/components/Brand";
import { fmtDate } from "@/i18n";
import StrategyRoomCTA from "@/components/workspace/StrategyRoomCTA";
import { profileKind } from "@/lib/businessIntegrity";

function Markdown({ text }) {
  return (
    <div className="report-body">
      {text.split("\n").map((line, i) => {
        const l = line.trim();
        if (!l) return null;
        if (l.startsWith("#")) return <h4 key={i}>{l.replace(/^#+\s*/, "")}</h4>;
        if (/^[-*]\s/.test(l)) return <li key={i}>{l.replace(/^[-*]\s/, "").replace(/\*\*/g, "")}</li>;
        return <p key={i}>{l.replace(/\*\*/g, "")}</p>;
      })}
    </div>
  );
}

export default function Reports({ lang, t, data, setData }) {
  const [reports, setReports] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/reports").then((r) => setReports(r.data)).catch(() => setReports([])); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/reports/generate", { lang });
      setReports((list) => [r.data, ...(list || [])]); setOpen(r.data.id);
      setData((d) => ({ ...d, counts: { ...d.counts, reports: (d.counts?.reports || 0) + 1 } }));
    } catch (err) { toast.error(errorText(err, "Could not generate the report.")); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    await api.delete(`/reports/${id}`);
    setReports((list) => list.filter((r) => r.id !== id)); if (open === id) setOpen(null);
    setData((d) => ({ ...d, counts: { ...d.counts, reports: Math.max(0, (d.counts?.reports || 1) - 1) } }));
  };

  return (
    <div className="section-page">
      <PageTitle eyebrow={t.eyebrows.reports} title={t.reports} sub={t.reportsSub}>
        <button className="primary-button" onClick={generate} disabled={busy} data-testid="generate-report-button"><Sparkles size={16} />{busy ? t.generating : t.generateReport}</button>
      </PageTitle>
      <p className="source-note">{t.reportsSource}</p>
      {profileKind(data?.business) === "demonstration" && <div className="integrity-banner"><strong>{t.demoProfile}</strong><p>{t.demoProfileNote}</p></div>}
      <StrategyRoomCTA t={t} testId="reports-strategy-room" />
      {reports === null ? <p className="muted">{t.loading}</p> : reports.length === 0 ? (
        <div className="empty-state" data-testid="reports-empty"><div className="empty-icon"><FileText size={22} /></div><p>{t.noReports}</p><StrategyRoomCTA t={t} prominent testId="reports-empty-strategy" /></div>
      ) : (
        <div className="list" data-testid="reports-list">
          {reports.map((r) => (
            <article className={`list-item ${open === r.id ? "open" : ""}`} key={r.id} data-testid={`report-${r.id}`}>
              <div className="list-row">
                <div><strong>{r.title}</strong><small>{fmtDate(r.created_at, lang)} · {r.status === "Ready" ? t.reportReady : r.status} · {r.source === "fallback" ? t.reportFallback : t.reportModel}</small></div>
                <div className="row-actions">
                  <button className="quiet-button" onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`report-toggle-${r.id}`}>{open === r.id ? t.close : t.open}</button>
                  <button className="icon-button" onClick={() => remove(r.id)} aria-label={t.delete} data-testid={`report-delete-${r.id}`}><Trash2 size={15} /></button>
                </div>
              </div>
              {open === r.id && <Markdown text={r.content} />}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
